import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageBanner from "@/components/feature/PageBanner";
import { supabase } from "@/lib/supabase";
import { rubberMaterials } from "@/mocks/technical";

interface TechnicalDownload {
  id: string;
  title: string;
  category: string;
  file_type: string;
  file_size: string;
  date: string;
}

export default function TechnicalPage() {
  const [activeMaterial, setActiveMaterial] = useState("EPDM");
  const [activeTab, setActiveTab] = useState<"materials" | "downloads">("materials");

  const [downloads, setDownloads] = useState<TechnicalDownload[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");

  const selectedMaterial = rubberMaterials.find((m) => m.code === activeMaterial);
  const [fileSearch, setFileSearch] = useState("");

  const fetchDownloads = async () => {
    const { data, error } = await supabase
      .from("technical_downloads")
      .select("*")
      .order("date", { ascending: false });

    if (!error && data) setDownloads(data as TechnicalDownload[]);
    else if (error) setFetchError(error.message);
  };

  useEffect(() => {
    setLoading(true);
    setFetchError("");
    fetchDownloads().finally(() => setLoading(false));
  }, []);

  const filteredDownloads = downloads.filter(
    (f) => !fileSearch || f.title.includes(fileSearch) || f.category.includes(fileSearch)
  );

  return (
    <SiteLayout navVariant="solid">
      <PageBanner
        title="기술자료"
        subtitle="고무 소재 전문 지식과 기술 노하우를 공유합니다"
        breadcrumb={[{ label: "기술자료" }]}
        bgImage="https://readdy.ai/api/search-image?query=Rubber%20material%20testing%20laboratory%20with%20hardness%20testers%20tensile%20testing%20machines%20and%20technical%20documents%2C%20clean%20organized%20engineering%20workspace%2C%20professional%20industrial%20laboratory%2C%20soft%20natural%20lighting%2C%20scientific%20precision%20environment&width=1600&height=680&seq=krubber-technical-banner&orientation=landscape"
      />

      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-center gap-2 mb-12">
            <button
              type="button"
              onClick={() => setActiveTab("materials")}
              className={`px-6 py-3 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === "materials"
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              <i className="ri-flask-line mr-2" />
              고무 재질 소개
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("downloads")}
              className={`px-6 py-3 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === "downloads"
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              <i className="ri-download-2-line mr-2" />
              기술자료 다운로드
            </button>
          </div>

          {activeTab === "materials" && (
            <div>
              <div className="flex flex-wrap gap-2 mb-10 justify-center">
                {rubberMaterials.map((m) => (
                  <button
                    key={m.code}
                    type="button"
                    onClick={() => setActiveMaterial(m.code)}
                    className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                      activeMaterial === m.code
                        ? "bg-foreground-950 text-background-50"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    {m.code}
                  </button>
                ))}
              </div>

              {selectedMaterial && (
                <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
                  <div className="lg:w-3/5">
                    <div className="flex items-center gap-3 mb-4">
                      <span className={`w-10 h-10 rounded-xl ${selectedMaterial.color} flex items-center justify-center`}>
                        <i className={`${selectedMaterial.icon} text-background-50 text-lg`} />
                      </span>
                      <div>
                        <h2 className="text-2xl font-heading font-extrabold text-foreground-950">
                          {selectedMaterial.name}
                        </h2>
                        <p className="text-xs text-foreground-500">{selectedMaterial.fullName}</p>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mt-4">{selectedMaterial.desc}</p>

                    <h3 className="font-heading font-bold text-foreground-950 mt-8 mb-3">주요 특징</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {selectedMaterial.features.map((f) => (
                        <div key={f} className="flex items-start gap-2 p-3 rounded-lg bg-background-100">
                          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-check-line text-primary-500 text-sm" />
                          </span>
                          <span className="text-sm text-foreground-700">{f}</span>
                        </div>
                      ))}
                    </div>

                    <h3 className="font-heading font-bold text-foreground-950 mt-8 mb-3">적용 분야</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedMaterial.applications.map((app) => (
                        <span
                          key={app}
                          className="px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium"
                        >
                          {app}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="lg:w-2/5">
                    <div className="p-6 rounded-2xl bg-background-100 border border-background-200/70">
                      <h3 className="font-heading font-bold text-sm text-foreground-950 mb-4">물성 특성</h3>
                      <div className="flex flex-col gap-3">
                        {Object.entries(selectedMaterial.properties).map(([key, value]) => (
                          <div key={key} className="flex items-center justify-between">
                            <span className="text-xs font-medium text-foreground-600">{key}</span>
                            <span className="text-xs font-semibold text-foreground-900">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Link
                      to="/contact"
                      className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                    >
                      이 소재로 문의하기
                      <i className="ri-arrow-right-line" />
                    </Link>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "downloads" && (
            <div>
              {fetchError && (
                <div className="mb-6 px-5 py-3 rounded-xl bg-red-50 text-red-700 border border-red-200 text-sm text-center">
                  데이터를 불러오지 못했습니다.
                  <button
                    type="button"
                    onClick={() => { setFetchError(""); setLoading(true); fetchDownloads().finally(() => setLoading(false)); }}
                    className="ml-3 underline cursor-pointer font-semibold"
                  >
                    다시 시도
                  </button>
                </div>
              )}

              {loading ? (
                <div className="flex items-center justify-center py-20 text-foreground-400">
                  <span className="w-5 h-5 flex items-center justify-center animate-spin mr-2">
                    <i className="ri-loader-4-line" />
                  </span>
                  불러오는 중...
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-8">
                    <p className="text-sm text-foreground-600">
                      총 <strong className="text-foreground-950">{filteredDownloads.length}</strong>건의 자료
                    </p>
                    <div className="relative w-full sm:w-[280px]">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400">
                        <i className="ri-search-line text-sm" />
                      </span>
                      <input
                        type="text"
                        placeholder="자료명, 카테고리 검색"
                        value={fileSearch}
                        onChange={(e) => setFileSearch(e.target.value)}
                        className="w-full pl-9 pr-4 py-2.5 rounded-full bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    {filteredDownloads.length === 0 ? (
                      <div className="text-center py-16 text-foreground-400 text-sm">
                        검색 결과가 없습니다.
                      </div>
                    ) : (
                      filteredDownloads.map((file) => (
                        <div
                          key={file.id}
                          className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-5 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200/60 transition-colors group"
                        >
                          <div className="flex items-start gap-4">
                            <span className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                              <i className="ri-file-pdf-2-line text-primary-600" />
                            </span>
                            <div>
                              <p className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                                {file.title}
                              </p>
                              <div className="flex flex-wrap items-center gap-3 mt-1.5">
                                <span className="text-xs text-foreground-500">{file.category}</span>
                                <span className="text-xs text-foreground-400">{file.file_type} · {file.file_size}</span>
                                <span className="text-xs text-foreground-400">{file.date}</span>
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ml-14 sm:ml-0"
                          >
                            <i className="ri-download-2-line" />
                            다운로드
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}